import { useEffect, useRef } from 'react';
import { websocketManager } from '../lib/WebSocketManager';

/**
 * WebSocket 초기화를 위한 커스텀 Hook
 * - 인증 완료 후 WebSocket 구독 (한 번만)
 * - 주기적 토큰 검증 시 WebSocket 재초기화하지 않음 (핵심!)
 * - 중복 구독 방지
 * - 컴포넌트 언마운트 시 정리
 * 
 * @param {Object} options
 * @param {boolean} options.isVerifying - 검증 중 상태
 * @param {boolean} options.isAuthenticated - 인증 완료 상태
 */
export const useWebSocketInit = ({ isVerifying, isAuthenticated }) => {
  const hasSubscribedRef = useRef(false);
  const isVerifyingRef = useRef(isVerifying);
  const isAuthenticatedRef = useRef(isAuthenticated);

  // 최신 상태 동기화
  useEffect(() => {
    isVerifyingRef.current = isVerifying;
    isAuthenticatedRef.current = isAuthenticated;
  }, [isVerifying, isAuthenticated]);

  // ✅ 마운트 시 한 번만 실행 (의존성 배열 비어있음)
  useEffect(() => {
    let checkInterval;

    // 인증 완료를 기다리는 함수
    const waitForAuthAndSubscribe = () => {
      // 이미 구독했으면 체크 중단
      if (hasSubscribedRef.current) {
        if (checkInterval) {
          clearInterval(checkInterval);
        }
        return;
      }

      // 검증 완료되고 인증되었으면 WebSocket 초기화
      if (!isVerifyingRef.current && isAuthenticatedRef.current) {
        console.log('🔌 [useWebSocketInit] Authentication complete, initializing WebSocket');
        websocketManager.subscribe();
        hasSubscribedRef.current = true;
        if (checkInterval) {
          clearInterval(checkInterval);
        }
      }
    };

    // 초기 체크
    waitForAuthAndSubscribe();

    // 아직 초기화되지 않았으면 주기적으로 체크 (500ms마다)
    if (!hasSubscribedRef.current) {
      checkInterval = setInterval(waitForAuthAndSubscribe, 500);
    }

    // Cleanup: 컴포넌트 언마운트 시
    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      if (hasSubscribedRef.current) {
        console.log('🔌 [useWebSocketInit] Component unmounting, cleaning up WebSocket');
        websocketManager.unsubscribe();
        hasSubscribedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ 빈 배열: 마운트 시 한 번만 실행, ref를 통해 최신 상태 참조

  // ✅ 로그아웃 감지 (isAuthenticated가 false가 되면 WebSocket 해제)
  useEffect(() => {
    if (!isAuthenticated && hasSubscribedRef.current) {
      console.log('🔌 [useWebSocketInit] User logged out, unsubscribing WebSocket');
      websocketManager.unsubscribe();
      hasSubscribedRef.current = false;
    }
  }, [isAuthenticated]);
};
